import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import GradientBlue from "../../Components/InputFields/GradientBlue";
import Simple from "../../Components/InputFields/Simple";
import Spinner from "../../Components/Loader/Spinner";
import LogoWhite10 from "../../Media/PNG/LogoWhite10.svg";
import LogoBlue from "../../Media/PNG/LogoBlue.svg";
import { API_BASE_URL } from "../../config/api";
import Input from "./Components/Input";
import Back from "./Components/Back";
import { GOOGLE_CREDENTIAL_STORAGE_KEY } from "./Components/GoogleAuthButton";
import LiquidGlass from "../../Components/Style/LiquidGlass";
import HeroBG from "../../Components/Style/HeroBG";
import OtpCode from "../../Components/InputFields/OtpCode";

const DEFAULT_SIGNUP_DATA = {
  phone_number: "",
  fn: "",
  ln: "",
  referral_code: "",
};

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const PREVIEW_GOOGLE_SIGNUP_FORM = process.env.REACT_APP_ENV === "local";

function toEnglishDigits(value) {
  return String(value || "")
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)));
}

function omitFalsyFields(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => Boolean(value)),
  );
}

function getCredential(search) {
  const params = new URLSearchParams(search);
  return (
    params.get("credential") ||
    params.get("credentials") ||
    sessionStorage.getItem(GOOGLE_CREDENTIAL_STORAGE_KEY) ||
    ""
  );
}

function getResponseData(result) {
  return result?.data || result || {};
}

function getErrorText(errorData) {
  if (!errorData) {
    return "درخواست انجام نشد. لطفاً دوباره تلاش کنید.";
  }

  if (typeof errorData.detail === "string") {
    return errorData.detail;
  }

  if (typeof errorData.message === "string") {
    return errorData.message;
  }

  const firstError = Object.values(errorData).find((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  });

  if (Array.isArray(firstError)) {
    return firstError[0];
  }

  if (typeof firstError === "string") {
    return firstError;
  }

  return "درخواست انجام نشد. لطفاً دوباره تلاش کنید.";
}

function storeAuthData(data) {
  localStorage.setItem("refresh", data.refresh);
  localStorage.setItem("access", data.access);
  localStorage.setItem("role", data.role);
  localStorage.setItem("email", data.email);

  if (data.uuid) {
    localStorage.setItem("uuid", data.uuid);
  }
  if (data.username) {
    localStorage.setItem("username", data.username);
  }

  localStorage.removeItem("authCredentials");
  sessionStorage.removeItem(GOOGLE_CREDENTIAL_STORAGE_KEY);
}

async function postGoogleLogin(payload) {
  const response = await fetch(`${API_BASE_URL}auth/trader/login/google/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => ({}));
  return { response, result, data: getResponseData(result) };
}

function PageFrame({ children }) {
  return (
    <div className="relative flex justify-center items-center min-h-dvh bg-zi-bg1-light dark:bg-zi-bg1-dark p-4">
      {children}
    </div>
  );
}

function LoadingCard() {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;

  return (
    <LiquidGlass tag="div" rounded="3xl" className="rounded-3xl max-w-sm w-full flex z-10" childrenClassName="w-full">
      <div className="w-full max-w-sm bg-zi-bg2-light dark:bg-zi-inputbg-dark/50 rounded-3xl p-5 gap-7 flex flex-col items-center">
        <div className="h-14">
          <img src={logoSrc} alt="لوگو" className="w-14" />
        </div>
        <Spinner color="#1f8fff" />
        <p className="font-light text-sm text-zi-text-light dark:text-zi-text-dark">
          در حال بررسی ورود با گوگل
        </p>
      </div>
    </LiquidGlass>
  );
}

function GoogleTwoFaStep({ credential, onAuthSuccess }) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [inputNums, setInputNums] = useState(["", "", "", "", "", ""]);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  const handleSubmit = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (submitInFlightRef.current) {
      return;
    }

    const otp = inputNums.join("");
    if (otp.length < 6) {
      setCodeInvalid(true);
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setCodeInvalid(false);

    try {
      const { response, data } = await postGoogleLogin({
        credential,
        credentials: credential,
        otp,
      });

      if (!response.ok || !data.auth || !data.access || !data.refresh) {
        setCodeInvalid(true);
        return;
      }

      onAuthSuccess(data);
    } catch (error) {
      console.error("Google 2FA request failed:", error);
      setCodeInvalid(true);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [credential, inputNums, onAuthSuccess]);

  useEffect(() => {
    if (inputNums.every((num) => num !== "")) {
      handleSubmit();
    }
  }, [handleSubmit, inputNums]);

  return (
    <LiquidGlass tag="div" rounded="3xl" className="rounded-3xl max-w-sm w-full flex z-10" childrenClassName="w-full">
      <div className="w-full max-w-sm bg-zi-bg2-light dark:bg-zi-inputbg-dark/50 rounded-3xl p-5 gap-7 flex flex-col">
        <div className="flex flex-col gap-2 justify-center items-center">
          <div className="h-14">
          <img src={logoSrc} alt="لوگو" className="w-14" />
        </div>
          <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
            کوژیتو فاند
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
              کد دو عاملی را وارد کنید
            </h1>
            <Back />
          </div>
          <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
            کدی که در Google Authenticator می‌بینید را وارد کنید.
          </span>
        </div>

        <form className="flex flex-col gap-4 items-end" onSubmit={handleSubmit}>
          <OtpCode
            values={inputNums}
            setValues={setInputNums}
            onValueChange={() => setCodeInvalid(false)}
            inputClassName="size-full aspect-square rounded-lg caret-zi-blue-light border border-zi-border-light dark:border-none bg-zi-bg2-light dark:bg-zi-inputbg-dark hocus-within:bg-zi-bg1-light text-center text-xl font-medium text-zi-text-light dark:text-zi-text-dark outline-none hocus-within:ring-1 hocus-within:ring-zi-border-light hocus-within:dark:ring-zi-border-dark [direction:ltr]"
          />

          <button
            type="button"
            className="text-zi-blue-light text-xs hocus:underline bg-transparent"
          >
            به کد دو عاملی دسترسی ندارید؟
          </button>
        </form>
        {codeInvalid && (
          <p className="text-amber-400 text-sm">کد وارد شده صحیح نمی‌باشد</p>
        )}

        <GradientBlue
          type="submit"
          value="ورود"
          isLoader={isSubmitting}
          onClick={handleSubmit}
        />
      </div>
    </LiquidGlass>
  );
}

function GoogleInfoStep({ credential, signupData, setSignupData, onBack, onAuthSuccess }) {
  const [isDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  const logoSrc = isDark ? LogoWhite10 : LogoBlue;
  const [fieldsRequired, setFieldsRequired] = useState(false);
  const [invalidPhoneNumber, setInvalidPhoneNumber] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    const value =
      field === "phone_number"
        ? toEnglishDigits(event.target.value).replace(/\D/g, "")
        : event.target.value;
    setSignupData((previousData) => ({ ...previousData, [field]: value }));
    setFieldsRequired(false);
    setInvalidPhoneNumber(false);
    setSignupError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phoneNumber = (signupData.phone_number || "").trim();
    const trimmedFn = (signupData.fn || "").trim();
    const trimmedLn = (signupData.ln || "").trim();

    if (!phoneNumber || !trimmedFn || !trimmedLn) {
      setFieldsRequired(true);
      return;
    }

    if (!/^09\d{9}$/.test(phoneNumber)) {
      setInvalidPhoneNumber(true);
      return;
    }

    const requestPayload = omitFalsyFields({
      credential,
      credentials: credential,
      phone_number: phoneNumber,
      fn: trimmedFn,
      ln: trimmedLn,
      referral_code: signupData.referral_code,
    });

    setLoading(true);
    setSignupError("");
    try {
      const { response, result, data } = await postGoogleLogin(requestPayload);

      if (!response.ok || !data.auth || !data.access || !data.refresh) {
        setSignupError(getErrorText(result));
        return;
      }

      onAuthSuccess(data);
    } catch (error) {
      console.error("Google signup request failed:", error);
      setSignupError("ثبت نام انجام نشد. لطفاً دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LiquidGlass tag="div" rounded="3xl" className="rounded-3xl max-w-sm w-full flex z-10" childrenClassName="w-full">
      <div className="w-full max-w-sm bg-zi-bg2-light dark:bg-zi-inputbg-dark/50 rounded-3xl p-5 gap-7 flex flex-col">
        <div className="flex flex-col gap-2 justify-center items-center">
          <div className="h-14">
          <img src={logoSrc} alt="لوگو" className="w-14" />
        </div>
          <p className="font-medium text-sm text-zi-text-light dark:text-zi-text-dark">
            کوژیتو فاند
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
              ایجاد حساب کاربری
            </h1>
            <Back onClick={onBack} />
          </div>
          <span className="text-xs font-light text-zi-text-light/50 dark:text-zi-text-dark/50">
            مرحله نهایی: شماره همراه و اطلاعات شخصی
          </span>
        </div>

        <div>
          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <Simple
              placeholder="شماره تلفن همراه"
              type="tel"
              id="phoneNumber"
              isNumeric={true}
              formValue={signupData.phone_number || ""}
              formOnChange={handleChange("phone_number")}
            />
            <Input
              label="نام"
              value={signupData.fn || ""}
              type="text"
              id="fn"
              name="fn"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleChange("fn")}
            />
            <Input
              label="نام خانوادگی"
              value={signupData.ln || ""}
              type="text"
              id="ln"
              name="ln"
              isReadOnly={false}
              isAuotComplete={true}
              onChange={handleChange("ln")}
            />
            <GradientBlue
              type="submit"
              value="ایجاد"
              isLoader={loading}
              onClick={handleSubmit}
            />
          </form>
          {fieldsRequired && (
            <p className="text-amber-400 text-sm font-extralight mt-1">
              لطفا شماره همراه، نام و نام خانوادگی را وارد کنید
            </p>
          )}
          {invalidPhoneNumber && (
            <p className="text-amber-400 text-sm font-extralight mt-1">
              شماره تلفن همراه وارد شده معتبر نیست
            </p>
          )}
          {signupError && (
            <p className="text-amber-400 text-sm font-extralight mt-1">
              {signupError}
            </p>
          )}
        </div>
      </div>
    </LiquidGlass>
  );
}

export default function GoogleAuthRedirect() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const credential = useMemo(() => getCredential(search), [search]);
  const urlRef = new URLSearchParams(search).get("ref") || "";
  const [step, setStep] = useState("loading");
  const [error, setError] = useState("");
  const [signupData, setSignupData] = useState({
    ...DEFAULT_SIGNUP_DATA,
    referral_code: urlRef,
  });
  const initialRequestStarted = useRef(false);

  const handleAuthSuccess = useCallback((data) => {
    storeAuthData(data);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (initialRequestStarted.current) {
      return;
    }
    initialRequestStarted.current = true;

    if (!credential) {
      if (PREVIEW_GOOGLE_SIGNUP_FORM) {
        setStep("signupInfo");
        return;
      }

      setError("اطلاعات ورود گوگل دریافت نشد. لطفاً دوباره تلاش کنید.");
      setStep("error");
      return;
    }

    async function loginWithGoogle() {
      setStep("loading");
      setError("");
      try {
        const { response, result, data } = await postGoogleLogin({
          credential,
          credentials: credential,
        });

        if (!response.ok) {
          setError(getErrorText(result));
          setStep("error");
          return;
        }

        if (data.auth && data.twoFa && !data.access) {
          setStep("twoFa");
          return;
        }

        if (data.auth && data.access && data.refresh) {
          handleAuthSuccess(data);
          return;
        }

        if (data.signup_required) {
          setStep("signupInfo");
          return;
        }

        setError(getErrorText(result));
        setStep("error");
      } catch (requestError) {
        console.error("Google login request failed:", requestError);
        setError("ورود با گوگل انجام نشد. لطفاً دوباره تلاش کنید.");
        setStep("error");
      }
    }

    loginWithGoogle();
  }, [credential, handleAuthSuccess]);

  if (step === "loading") {
    return (
      <PageFrame>
        <div className="absolute inset-0 z-0 transition-none">
          <HeroBG />
        </div>
        <LoadingCard />
      </PageFrame>
    );
  }

  if (step === "twoFa") {
    return (
      <PageFrame>
        <div className="absolute inset-0 z-0 transition-none">
          <HeroBG />
        </div>
        <GoogleTwoFaStep
          credential={credential}
          onAuthSuccess={handleAuthSuccess}
        />
      </PageFrame>
    );
  }

  if (step === "signupInfo") {
    return (
      <PageFrame>
        <div className="absolute inset-0 z-0 transition-none">
          <HeroBG />
        </div>
        <GoogleInfoStep
          credential={credential}
          signupData={signupData}
          setSignupData={setSignupData}
          onBack={() => navigate("/signup")}
          onAuthSuccess={handleAuthSuccess}
        />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <div className="absolute inset-0 z-0 transition-none">
        <HeroBG />
      </div>
      <LiquidGlass tag="div" rounded="3xl" className="rounded-3xl max-w-sm w-full flex z-10" childrenClassName="w-full">
        <div className="w-full max-w-sm bg-zi-bg2-light dark:bg-zi-inputbg-dark/50 rounded-3xl p-5 gap-5 flex flex-col">
          <p className="text-amber-400 text-sm font-extralight">{error}</p>
          <GradientBlue
            type="button"
            value="بازگشت به ورود"
            onClick={() => navigate("/signin/email", { replace: true })}
          />
        </div>
      </LiquidGlass>
    </PageFrame>
  );
}
